import { apiFetch } from './apiFetch';

const handle = async <T>(res: Response) => {
  if (res.status === 204) return null as T;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed ${res.status}`);
  }
  return (await res.json()) as T;
};

export const listStrategies = async () => {
  const res = await apiFetch('/v1/strategies', { cache: 'no-cache' });
  return handle<any[]>(res);
};

export const getActiveStrategyId = async (): Promise<string | null> => {
  const res = await apiFetch('/v1/strategies/active', { cache: 'no-cache' });
  const payload = await handle<{ strategyId: string | null; isExplicit?: boolean }>(res);
  return payload?.isExplicit ? payload.strategyId ?? null : null;
};

export const getActiveStrategy = async (): Promise<{ strategyId: string | null; isExplicit: boolean }> => {
  const res = await apiFetch('/v1/strategies/active', { cache: 'no-cache' });
  if (res.status === 204) return { strategyId: null, isExplicit: false };
  const payload = await handle<{ strategyId: string | null; isExplicit?: boolean }>(res);
  return { strategyId: payload?.strategyId ?? null, isExplicit: Boolean(payload?.isExplicit) };
};

export const activateStrategy = async (id: string) => {
  const res = await apiFetch(`/v1/strategies/${encodeURIComponent(id)}/activate`, {
    method: 'POST',
  });
  return handle<{ strategyId: string; horizonKey?: string }>(res);
};

export const createStrategy = async (payload: { title: string; description?: string; timeHorizonMonths?: number }) => {
  const res = await apiFetch('/v1/strategies', {
    method: 'POST',
    json: payload,
  });
  return handle<any>(res);
};

export const getStrategy = async (id: string) => {
  const res = await apiFetch(`/v1/strategies/${id}`, { cache: 'no-cache' });
  return handle<any>(res);
};

export const updateStrategy = async (id: string, payload: { title?: string; description?: string; timeHorizonMonths?: number }) => {
  const res = await apiFetch(`/v1/strategies/${id}`, {
    method: 'PUT',
    json: payload,
  });
  return handle<any>(res);
};

export const deleteStrategy = async (id: string) => {
  const res = await apiFetch(`/v1/strategies/${id}`, { method: 'DELETE' });
  return handle<any>(res);
};

export const listDesignPrinciples = async (strategyId: string) => {
  const res = await apiFetch(`/v1/strategies/${strategyId}/design-principles`, { cache: 'no-cache' });
  return handle<any[]>(res);
};

export const createDesignPrinciple = async (strategyId: string, payload: { title: string; description?: string; category?: string }) => {
  const res = await apiFetch(`/v1/strategies/${strategyId}/design-principles`, {
    method: 'POST',
    json: payload,
  });
  return handle<any>(res);
};

export const deleteDesignPrinciple = async (id: string) => {
  const res = await apiFetch(`/v1/strategies/design-principles/${id}`, { method: 'DELETE' });
  return handle<any>(res);
};

export const listStrategyCapabilities = async (strategyId: string) => {
  const res = await apiFetch(`/v1/strategies/${strategyId}/capabilities`, { cache: 'no-cache' });
  return handle<any[]>(res);
};

export const createStrategyCapability = async (
  strategyId: string,
  payload: { name: string; description: string; category: string; criticality?: number },
) => {
  const res = await apiFetch(`/v1/strategies/${strategyId}/capabilities`, {
    method: 'POST',
    json: payload,
  });
  return handle<any>(res);
};

export const deleteStrategyCapability = async (id: string) => {
  const res = await apiFetch(`/v1/strategies/capabilities/${id}`, { method: 'DELETE' });
  return handle<any>(res);
};

export const buildNeuroTruth = async (strategyId: string) => {
  const res = await apiFetch('/v1/neuro/truth', {
    method: 'POST',
    json: { strategyId },
  });
  return handle<any>(res);
};

export const commitNeuroTruth = async (strategyId: string, truth: any) => {
  const res = await apiFetch('/v1/neuro/truth/commit', {
    method: 'POST',
    json: { strategyId, truth },
  });
  return handle<any>(res);
};
